import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { apiPost } from '../lib/api';
import { AuthLayout } from '../components/layout/AuthLayout';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token provided.');
      return;
    }
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    apiPost('/customer/auth/verify-email', { token }, { skipAuth: true })
      .then(() => setStatus('success'))
      .catch((err) => {
        setStatus('error');
        const data = (err as { data?: { error?: string } })?.data;
        setErrorMessage(data?.error || 'Invalid or expired verification link.');
      });
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendLoading(true);
    try {
      await apiPost('/customer/auth/verify-email/resend', { email: resendEmail.trim() }, { skipAuth: true });
      setResendSent(true);
    } catch {
      // Silently handle — we don't reveal whether the email exists
      setResendSent(true);
    } finally {
      setResendLoading(false);
    }
  };

  if (status === 'loading') {
    return (
      <AuthLayout title="Verifying email" subtitle="Please wait...">
        <div className="flex flex-col items-center gap-4 py-8">
          <Loader2 size={32} className="animate-spin text-zinc-400" />
          <p className="text-sm text-zinc-500">Verifying your email address...</p>
        </div>
      </AuthLayout>
    );
  }

  if (status === 'success') {
    return (
      <AuthLayout title="Email verified" subtitle="You're all set!">
        <div className="space-y-5 text-center">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-6">
            <CheckCircle size={32} className="mx-auto mb-2 text-emerald-600" />
            <p className="text-sm font-medium text-emerald-800">
              Your email has been verified successfully!
            </p>
            <p className="mt-1 text-xs text-emerald-600">
              You can now sign in to your account.
            </p>
          </div>
          <Link to="/login" className="btn-primary w-full inline-flex items-center justify-center">
            Go to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Error state
  return (
    <AuthLayout title="Verification failed" subtitle="Something went wrong">
      <div className="space-y-5">
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-6 text-center">
          <XCircle size={32} className="mx-auto mb-2 text-red-500" />
          <p className="text-sm font-medium text-red-800">{errorMessage}</p>
        </div>

        {/* Resend section */}
        {!resendSent ? (
          <form onSubmit={handleResend} className="space-y-3">
            <p className="text-sm text-zinc-600 text-center">
              Need a new verification link? Enter your email below.
            </p>
            <input
              type="email"
              autoComplete="email"
              className="input-field"
              placeholder="you@example.com"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
            />
            <button
              type="submit"
              disabled={resendLoading || !resendEmail.trim()}
              className="btn-primary w-full"
            >
              {resendLoading ? (
                <><Loader2 size={16} className="animate-spin" /> Sending…</>
              ) : (
                'Resend verification email'
              )}
            </button>
          </form>
        ) : (
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-center">
            <p className="text-sm text-blue-700">
              If an account exists for that email, a new verification link has been sent.
            </p>
          </div>
        )}

        <p className="text-center text-sm text-zinc-500">
          <Link to="/login" className="font-semibold text-[--color-primary] hover:opacity-80">
            Back to sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
